import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useState, type ReactElement } from 'react';
import { Box, Flex, Grid, Text } from '@/components/common';
import { DistanceLabel } from '@/components/profile';
import { useCurrentUser } from '@/contexts';
import { useFeatureFlag } from '@/hooks';
import { analyticsClient } from '@/lib';
import { isStampedNote, noteService, type StampedNote } from '@/services';
import type { Note } from '@/types';
import { firstNameOf, firstPhotoUrl } from '@/utils';
import { NoteCircle, type NoteCircleVariant } from './NoteCircle';
import { NoteComposerDialog } from './NoteComposerDialog';
import { NoteReplyDialog } from './NoteReplyDialog';
import { OwnNoteActionsDialog } from './OwnNoteActionsDialog';

export type NotesLayout = 'grid' | 'row';

/** Placeholder circle while the stampNoteAuthor stamp is still in flight. */
function NoteCircleSkeleton() {
  return (
    <Flex
      direction="column"
      align="center"
      gap="2"
      className="note-circle-skeleton"
      aria-hidden
      data-testid="note-skeleton"
    >
      <Box className="note-skeleton-bubble" />
      <Box className="note-skeleton-avatar" />
      <Box className="note-skeleton-label" />
    </Flex>
  );
}

interface NotesCirclesProps {
  layout: NotesLayout;
  myNote: Note | null;
  entries: Note[];
}

export function NotesCircles({ layout, myNote, entries }: NotesCirclesProps) {
  const { t } = useTranslation('notes');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, currentUser } = useCurrentUser();
  const { enabled: replyFlow } = useFeatureFlag('notes_reply_flow');

  const [composerOpen, setComposerOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // The reply target lives in the URL so navigating back from the author
  // profile reopens the sheet on the same note instead of losing it.
  const replyUid = replyFlow ? searchParams.get('reply') : null;
  const replyTarget = replyUid
    ? (entries.find((note): note is StampedNote => isStampedNote(note) && note.uid === replyUid) ??
      null)
    : null;

  const setReplyParam = (uid: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (uid) next.set('reply', uid);
    else next.delete('reply');
    setSearchParams(next, { replace: true, state: location.state });
  };

  const viewerLive = currentUser?.availableToday ?? false;
  let ownVariant: NoteCircleVariant = 'none';
  if (!myNote) ownVariant = 'dashed';
  else if (viewerLive) ownVariant = 'outline';
  const ownColor = viewerLive && myNote ? 'red' : 'gray';
  const ownHighContrast = !myNote || !viewerLive;

  const handleOwnTap = () => (myNote ? setActionsOpen(true) : setComposerOpen(true));

  const handleOtherTap = (note: Note) => {
    // Unstamped notes render a skeleton and are not tappable.
    if (!isStampedNote(note)) return;
    // Flags default to profile navigation while loading.
    analyticsClient.capture('note_opened', {
      target: replyFlow ? 'reply' : 'profile',
      author_id: note.uid,
      session_id: analyticsClient.getCurrentSessionId(),
    });
    if (replyFlow) {
      setReplyParam(note.uid);
    } else {
      navigate(`/profile/${note.uid}`);
    }
  };

  const handleShare = async (text: string) => {
    if (!currentUser) return;
    try {
      await noteService.upsertNote(currentUser.uid, text);
      analyticsClient.capture('note_created', {
        source: 'composer',
        replaced: Boolean(myNote),
        session_id: analyticsClient.getCurrentSessionId(),
      });
    } catch (err) {
      console.error('Failed to share note:', err);
      toast.error(t('composer.error'));
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    try {
      await noteService.deleteNote(user.uid);
      analyticsClient.capture('note_deleted', {
        session_id: analyticsClient.getCurrentSessionId(),
      });
      setActionsOpen(false);
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error(t('actions.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const ownCircle = (
    <NoteCircle
      key="own"
      name={t('circle.you')}
      photoUrl={firstPhotoUrl(currentUser?.photos)}
      content={myNote?.text}
      color={ownColor}
      variant={ownVariant}
      highContrast={ownHighContrast}
      showLiveLabel={Boolean(myNote && viewerLive)}
      editable
      sublabel={
        !myNote ? (
          <Text size="1" color="gray">
            {t('circle.tapToWrite')}
          </Text>
        ) : undefined
      }
      onClick={handleOwnTap}
    />
  );

  const circles = entries.map(note => {
    if (!isStampedNote(note)) return <NoteCircleSkeleton key={note.uid} />;
    const live = Boolean(note.availableToday);
    const variant: NoteCircleVariant = live ? 'outline' : 'none';
    const color = live ? 'red' : 'black';
    return (
      <NoteCircle
        key={note.uid}
        name={firstNameOf(note.authorName)}
        photoUrl={note.authorPhotoUrl}
        content={note.text}
        color={color}
        variant={variant}
        showLiveLabel={live}
        sublabel={
          <DistanceLabel latitude={note.latitude} longitude={note.longitude} variant="ghost" />
        }
        onClick={() => handleOtherTap(note)}
      />
    );
  });

  let content: ReactElement;
  if (layout === 'grid') {
    content = (
      <Grid columns={{ initial: '3' }} gapX="3" gapY="6" justifyItems="center">
        {ownCircle}
        {circles}
      </Grid>
    );
  } else {
    content = (
      <Flex
        className="notes-strip"
        align="center"
        gap="3"
        overflowX="auto"
        pb="2"
        style={{ contain: 'inline-size' }}
      >
        {ownCircle}
        {circles}
      </Flex>
    );
  }

  return (
    <>
      {content}
      <NoteComposerDialog
        open={composerOpen}
        onOpenChange={setComposerOpen}
        onSubmit={handleShare}
      />
      <OwnNoteActionsDialog
        open={actionsOpen}
        onOpenChange={setActionsOpen}
        text={myNote?.text ?? ''}
        onNewNote={() => {
          setActionsOpen(false);
          setComposerOpen(true);
        }}
        onDelete={() => void handleDelete()}
        deleting={deleting}
      />
      <NoteReplyDialog note={replyTarget} onClose={() => setReplyParam(null)} />
    </>
  );
}
